<template>
  <div class="arcgis-wizard-container">
    <div class="wizard-nav">
      <div :class="['step-item', { active: currentArcgisStep === 1 }]" @click="goToStep(1)">1. Toc</div>

      <div v-if="!isImageServer" :class="['step-item', { active: currentArcgisStep === 2 }]" @click="goToStep(2)">2. Campos</div>
    </div>

    <div class="wizard-content">
      <component
        :is="currentStepComponent"
        ref="currentStep"
        v-model="currentDataSegment"
        :categories="categories"
        :current-layer-id="currentLayerId"
      />

      <template v-if="isEdit">
        <label for="checkbox-status">Estado</label>
        <div id="status">
          <input type="checkbox" id="checkbox-status" :checked="initialData.status !== false" @change="$emit('toggle-status', $event)">
          <label for="checkbox-status">Activa</label>
        </div>
      </template>

      <div class="container-action-left" style="justify-content: end; margin-top: 20px">
        <button class="btn btn-secondary" @click="prevStep" :disabled="currentArcgisStep === 1 || isLoading">
          Anterior
        </button>

        <button class="btn btn-primary" @click="nextStep" :disabled="isLoading">
          <span v-if="isLoading">Cargando...</span>
          <span v-else>{{ currentArcgisStep === 2 || isImageServer ? "Finalizar Paso" : "Siguiente" }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FormularioArcgisPaso1 from "./FormularioArcgisPaso1.vue";
import FormularioArcgisPaso2 from "./FormularioArcgisPaso2.vue";
import { restLayer } from "@/components/mixin/restLayer.js";
import { formatter } from "@/components/mixin/formatter.js";
import { fetchArcgisMetadata } from "@/services/layers/arcgisMetadata.js";

export default {
  name: "FormularioArcgis",
  mixins: [formatter, restLayer],
  components: {
    FormularioArcgisPaso1,
    FormularioArcgisPaso2,
  },

  props: {
    categories: Array,
    layerCreationMode: String,
    isEdit: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      currentArcgisStep: 1,
      isLoading: false,

      formData: {
        nombre: "",
        descripcion: "",
        url: "",
        codigo: "",
        fuente: "",
        fechaDato: "",
        fechaActualizacion: "",
        geometryType: "",

        esVulnerable: "NO",
        reportIconSvg: "",
        capPoblacion: "NO",
        orden: 0,
        layerOrderIds: null,
        categoria: "-1",
      },
      fieldsArcgis: [],
    };
  },
  
  watch: {
    initialData: {
      handler(newVal) {
        if (this.isEdit && newVal) {
          this.populateForm(newVal);
        }
      },
      deep: true,
      immediate: true,
    }
  },

  computed: {
    currentLayerId() {
      if (!this.isEdit) return "";
      return this.initialData.id || this.initialData._id || this.$route.params.idLayer || "";
    },
    isImageServer() {
      return /\/imageserver$/i.test((this.formData.url || "").trim());
    },
    currentStepComponent() {
      return this.currentArcgisStep === 1
        ? "FormularioArcgisPaso1"
        : "FormularioArcgisPaso2";
    },
    currentDataSegment: {
      get() {
        return this.currentArcgisStep === 1 ? this.formData : this.fieldsArcgis;
      },
      set(newValue) {
        if (this.currentArcgisStep === 1) {
          this.formData = newValue;
        } else {
          this.fieldsArcgis = newValue;
        }
      },
    },
  },

  methods: {
    // Los avisos van por el toast del sistema y no por alert(): alert bloquea la pestaña
    // completa y no se parece a nada del resto del visor.
    notify(message, type = "warning") {
      this.$store.dispatch("toast/show", { message, type });
    },
    populateForm(data) {
        this.formData = {
            nombre: data.name || "",
            descripcion: data.desc || "",
            url: data.provider ? data.provider.url : "",
            codigo: data.codigo || "",
            fuente: data.fuente || "",
            fechaDato: this.dateInputFormatter(data.fecha_dato),
            fechaActualizacion: this.dateInputFormatter(data.fecha_actualizacion),
            geometryType: data.geometry_type || data.geometryType || "",
            esVulnerable: data.esVulnerable || data.es_vulnerable || "NO", // Handle both casing if unstable
            reportIconSvg: data.reportIconSvg || data.report_icon_svg || "",
            // El backend recibe capPoblacion (dto/arcgis.go) pero responde cap_poblacion
            // (models/layer.go). Leer sólo la grafía de entrada dejaba el campo en "NO" al abrir
            // el formulario, y guardar lo pisaba: una capa de población dejaba de serlo.
            capPoblacion: data.capPoblacion || data.cap_poblacion || "NO",
            orden: data.orden ? parseInt(data.orden) : 0,
            layerOrderIds: null,
            categoria: data.id_category || "-1",
        };
        // Populate fields if available, otherwise they might need to be fetched or ignored
        if (data.campos) {
            this.fieldsArcgis = data.campos.map(f => ({
                name: f.name,
                alias: f.alias,
                order: f.order,
                visible: f.visible,
                tooltip: f.tooltip,
                reporte: f.reporte,
                titulo: f.titulo
            }));
        }
    },
    async nextStep() {
      console.log("Next step clicked", this.formData);

      if (this.currentArcgisStep === 1) {
        // --- VALIDACIONES PASO 1 ---
        const { nombre, descripcion, categoria, url } = this.formData;

        if (!nombre || !descripcion) {
          this.notify("Completa los campos obligatorios: Nombre y Descripción.");
          return;
        }

        if (!categoria || categoria === "-1") {
          this.notify("Debes seleccionar una categoría.");
          return;
        }

        // Validación URL idéntica al componente
        if (!url) {
          this.notify("Debes ingresar la URL del servicio.");
          return;
        }
        if (!/^https?:\/\//i.test(url)) {
          this.notify("La URL debe comenzar con http:// o https://");
          return;
        }
        if (url.endsWith("/")) {
          this.notify("La URL no debe terminar en '/' (slash)");
          return;
        }
        if (/\/\d+$/.test(url)) {
          this.notify("La URL no debe terminar con el índice (ej: /0). Colócalo en el campo 'Código'.");
          return;
        }

        // --- Fin Validaciones ---

        if (this.isImageServer) {
          this.isLoading = true;
          try {
            const data = await fetchArcgisMetadata(this.formData.url + "?f=json");
            if (data.error) {
              throw new Error(data.error.message || "Error devuelto por ArcGIS.");
            }
            if (data.pixelType === undefined && data.bandCount === undefined) {
              throw new Error("La URL no corresponde a un servicio ImageServer válido.");
            }
            this.formData.codigo = "";
            this.fieldsArcgis = [];
            const finalData = this.getData();
            if (this.isEdit) {
              this.putLayerArcgis(finalData);
            } else {
              this.postLayerArcgis(finalData);
            }
          } catch (error) {
            console.error("Error validando servicio ImageServer:", error);
            this.notify("No se pudo validar el servicio ImageServer: " + error.message, "error");
          } finally {
            this.isLoading = false;
          }
          return;
        }

        // --- Lógica del Paso 1: Cargar campos desde ArcGIS ---
        // Al editar el mismo servicio se conservan los campos guardados y se agregan
        // los del servicio que no estén configurados (ocultos), para poder habilitarlos.
        const keepStored = this.isEdit && this.fieldsArcgis.length > 0 &&
          this.formData.url === this.initialData.provider.url &&
          String(this.formData.codigo) === String(this.initialData.codigo || "");

        this.isLoading = true;
        try {
          // Construir URL: url + /codigo + ?f=json
          let fetchUrl = this.formData.url;
          if (this.formData.codigo) {
            fetchUrl += "/" + this.formData.codigo;
          }
          fetchUrl += "?f=json";

          console.log("Fetching fields from:", fetchUrl);
          const data = await fetchArcgisMetadata(fetchUrl);

          if (data.error) {
            throw new Error(data.error.message || "Error devuelto por ArcGIS.");
          }

          if (!data.fields || !Array.isArray(data.fields)) {
            // Intento de fallback
            throw new Error("No se encontraron 'fields' en la respuesta JSON.");
          }

          // El backend lo normaliza a point/line/polygon. Guardarlo permite
          // que el geoproceso solicite geometría solo para capas puntuales.
          this.formData.geometryType = data.geometryType || "";

          // Mapear campos
          const stored = keepStored ? this.fieldsArcgis : [];
          const storedNames = stored.map(f => f.name);
          // `order` guardado puede tener huecos (orden del TOC antiguo): los nuevos van después del mayor.
          const lastOrder = Math.max(stored.length, ...stored.map(f => Number(f.order) || 0));
          const nuevos = data.fields
            .filter(f => storedNames.indexOf(f.name) === -1)
            .map((f, index) => ({
              name: f.name,
              alias: f.alias || f.name,
              order: lastOrder + index + 1,
              visible: !keepStored,
              tooltip: false,
              reporte: false,
              titulo: false,
            }));
          this.fieldsArcgis = stored.concat(nuevos);

          console.log("Campos cargados:", this.fieldsArcgis);
          this.currentArcgisStep++;

        } catch (error) {
          console.error("Error fetching ArcGIS fields:", error);
          if (keepStored) {
            this.notify("No se pudieron obtener los campos del servicio; se muestran los campos guardados.");
            this.currentArcgisStep++;
          } else {
            this.notify("No se pudieron obtener los campos de la capa: " + error.message, "error");
          }
        } finally {
          this.isLoading = false;
        }

      } else {
        const finalData = this.getData();
        console.log("✅ DATOS FINALES LISTOS PARA ENVIAR (Paso 2):");
        console.log("TOC (Paso 1):", finalData.toc);
        console.log("CAMPOS (Paso 2 - Tabla Editada):", finalData.campos);
        
        if (this.isEdit) {
            this.putLayerArcgis(finalData);
        } else {
            this.postLayerArcgis(finalData);
        }
      }
    },

    prevStep() {
      if (this.currentArcgisStep > 1) this.currentArcgisStep--;
    },

    // Los encabezados del wizard se mostraban con cursor:pointer pero sin
    // click. Avanzar a "2. Campos" pasa por nextStep() para conservar las
    // validaciones y la carga de campos del paso 1; volver a "1. Toc" es el
    // mismo prevStep() del botón Anterior.
    goToStep(step) {
      if (step === this.currentArcgisStep || this.isLoading) return;
      if (step < this.currentArcgisStep) this.prevStep();
      else this.nextStep();
    },

    getData() {
      return {
        toc: {
          ...this.formData,
          fechaDato: this.optionalDatePayload(this.formData.fechaDato),
          fechaActualizacion: this.optionalDatePayload(this.formData.fechaActualizacion),
        },
        campos: [...this.fieldsArcgis],
      };
    },
  },
};
</script>

<style scoped>
.arcgis-wizard-container {
  display: flex;
  gap: 20px;
  width: 100%;
}

.wizard-content {
  flex-grow: 1;
  min-width: 0;
}

.step-item {
  padding: 10px;
  margin-bottom: 5px;
  cursor: pointer;
  background-color: #eee;
  border: 1px solid #ddd;
}

.step-item.active {
  background-color: #006fb3;
  color: white;
  font-weight: bold;
}
</style>
